/**
 * Scroll reveal, ported from the Claude Design canvas ("Setbox Home", 2026-09-16).
 *
 * The canvas hides every [data-reveal] element with an inline opacity: 0 and
 * animates it in on intersection, with a 1800ms timeout that force-shows
 * anything the observer never reached. That order is wrong for a storefront:
 * the content starts invisible in the markup, so a JS failure or a crawler
 * without script support gets a blank page for 1.8s or forever.
 *
 * Here the hiding is done BY this script, so no-JS and failed-JS both render
 * normally. Everything else matches the canvas: the same easing, duration,
 * rootMargin and threshold, and each element is unobserved once shown.
 */
(() => {
  const SELECTOR = '[data-reveal]';

  const reduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (reduced || !('IntersectionObserver' in window)) return;

  const show = (el) => {
    el.style.animation = 'sbx-reveal-up .75s cubic-bezier(.22,.7,.2,1) both';
    el.style.opacity = '';
  };

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        show(entry.target);
        obs.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
  );

  const register = (root) => {
    (root instanceof Element && root.matches(SELECTOR) ? [root] : []).forEach(arm);
    root.querySelectorAll?.(SELECTOR).forEach(arm);
  };

  function arm(el) {
    if (el.dataset.revealArmed) return;
    el.dataset.revealArmed = '1';

    // Anything already on screen at load is shown without the animation —
    // sliding the hero-adjacent content up on first paint reads as a glitch.
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) return;

    el.style.opacity = '0';
    observer.observe(el);
  }

  const start = () => register(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  // Theme editor re-renders sections in place; re-arm whatever it inserts.
  document.addEventListener('shopify:section:load', (event) => register(event.target));
})();
